import React from 'react';
import _ from 'lodash';
import { Box, Card, CardContent, Divider, Select, Stack, TextField, Button, MenuItem, InputLabel, FormControl, FormHelperText, Alert } from '@mui/material';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useMutation, useParameterizedQuery } from 'react-fetching-library';
import { formatDate } from '../../../../../../../services/functions';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DatePicker from '@mui/lab/DatePicker';
import { submitInvoiceAction, updateInvoiceNumberAction } from '../../../../../../../services/request';
import { AlertDialogContainer } from '../../../../../../../components-core/others/AlertDialogContainer';
import { Position } from './Position';
import BaseDataForm from './BaseDataForm';

export const CorrectiveReceiptForm = ({ order, invoiceBaseData, handleClose, season, module, editData, submitCallback }) => {
  const productsRef = React.useRef();

  const { mutate, error, loading } = useMutation(submitInvoiceAction);

  const { number, buyer, seller, receipts } = invoiceBaseData;

  const [showAlert, setShowAlert] = React.useState(false);
  const [severity, setSeverity] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [today, setToday] = React.useState(new Date());
  const [initProduct, setInitProduct] = React.useState(null);

  const validationSchema = Yup.object({
    number: Yup.string().required('To pole nie może być puste'),
    receipt_correction_for: Yup.string().required('To pole nie może być puste'),
    buyer_name: Yup.string().required('To pole nie może być puste'),
    buyer_city: Yup.string().required('To pole nie może być puste'),
    buyer_postal_code: Yup.string().required('To pole nie może być puste'),
    buyer_street_address: Yup.string().required('To pole nie może być puste'),
    seller_city: Yup.string().required('To pole nie może być puste'),
    seller_postal_code: Yup.string().required('To pole nie może być puste'),
    seller_street_address: Yup.string().required('To pole nie może być puste'),
    seller_company_name: Yup.string().required('To pole nie może być puste'),
    issue_date: Yup.date().required('To pole nie może być puste')
      .nullable()
  });

  const handleSubmit = async values => {
    const productErrors = productsRef.current.getAllErrors();
    if (productErrors.length === 0) {
      setSeverity('error');
      setTitle('Formularz zawiera błędy');
      setMessage('');
      setShowAlert(true);

      return;
    }
    let hasErrors = false;
    _.map(productErrors, obj => {
      if (obj.errors.length > 0 || obj.requiredFields.length > 0) {
        hasErrors = true;
      }
    });
    if (hasErrors) {
      setSeverity('error');
      setTitle('Formularz zawiera błędy');
      setMessage('');
      setShowAlert(true);

      return;
    }

    values.products = productsRef.current.getProducts();
    values.season = season;
    values.module = module;
    if (!_.isEmpty(editData)) {
      values.buyer_id = editData.buyer;
    }
    const { error: mutateError, payload } = await mutate(values, !_.isEmpty(editData) ? 'PUT' : 'POST', !_.isEmpty(editData) ? editData.id : undefined);
    if (!mutateError) {
      submitCallback(!_.isEmpty(editData) ? 'edit' : 'create', payload);
      formik.resetForm();
      handleClose();
    } else {
      setSeverity('error');
      setTitle('Formularz zawiera błędy');
      setMessage(payload.message || '');
      setShowAlert(true);
    }
  };

  const formik = useFormik({
    initialValues: {
      order: order.id,
      type: 6,
      number: !_.isNil(number) ? number : !_.isNil(editData) ? editData.number : '',
      buyer_name: !_.isNil(buyer) ? !_.isNil(buyer.name) ? buyer.name : '' : !_.isEmpty(editData) ? editData.buyer_name : '',
      buyer_city: !_.isNil(buyer) ? !_.isNil(buyer.address__city) ? buyer.address__city : '' : !_.isEmpty(editData) ? editData.buyer_city : '',
      buyer_postal_code: !_.isNil(buyer) ? !_.isNil(buyer.address__postal_code) ? buyer.address__postal_code : '' : !_.isEmpty(editData) ? editData.buyer_postal_code : '',
      buyer_street_address: !_.isNil(buyer) ? !_.isNil(buyer.address__street) ? buyer.address__street : '' : !_.isEmpty(editData) ? editData.buyer_street_address : '',
      buyer_nip: !_.isNil(buyer) ? !_.isNil(buyer.nip) ? buyer.nip : '' : !_.isEmpty(editData) ? editData.buyer_nip : '',
      seller_name: !_.isNil(seller) ? !_.isNil(seller.name) ? seller.name : '' : !_.isEmpty(editData) ? editData.seller_name : '',
      seller_city: !_.isNil(seller) ? !_.isNil(seller.address__city) ? seller.address__city : '' : !_.isEmpty(editData) ? editData.seller_city : '',
      seller_postal_code: !_.isNil(seller) ? !_.isNil(seller.address__postal_code) ? seller.address__postal_code : '' : !_.isEmpty(editData) ? editData.seller_postal_code : '',
      seller_street_address: !_.isNil(seller) ? !_.isNil(seller.address__street_address) ? seller.address__street_address : '' : !_.isEmpty(editData) ? editData.seller_street_address : '',
      seller_company_name: !_.isNil(seller) ? !_.isNil(seller.company__name) ? seller.company__name : '' : !_.isEmpty(editData) ? editData.seller_company_name : '',
      seller_nip: !_.isNil(seller) ? !_.isNil(seller.nip) ? seller.nip : '' : !_.isEmpty(editData) ? editData.seller_nip : '',
      bank_name: !_.isNil(seller) ? !_.isNil(seller.company__bank_accounts__name) ? seller.company__bank_accounts__name : '' : !_.isEmpty(editData) ? editData.bank_name : '',
      account_number: !_.isNil(seller) ? !_.isNil(seller.company__bank_accounts__account_number) ? seller.company__bank_accounts__account_number : '' : !_.isEmpty(editData) ? editData.account_number : '',
      payment_method: !_.isEmpty(editData) ? editData.payment_method : 'przedplata',
      remarks: !_.isEmpty(editData) ? editData.remarks : '',
      issue_date: !_.isEmpty(editData) ? editData.issue_date : formatDate(today),
      correction_reason: !_.isEmpty(editData) ? editData.correction_reason : '',
      receipt_correction_for: !_.isEmpty(editData) ? editData.receipt_correction_for : ''
    },
    validationSchema,
    validateOnChange: false,
    enableReinitialize: true,
    onSubmit: values => {
      handleSubmit(values);
    }
  });

  const { query } = useParameterizedQuery(updateInvoiceNumberAction);

  const updateInvoiceNumber = async issueDate => {
    const { payload, error, loading } = await query(order.id, 6, issueDate);
    if (!error && !loading) {
      formik.setFieldValue('number', payload.number);
    };
  };

  if (!_.isNil(receipts) && receipts.length === 0) {
    return (
      <Box sx={{ maxHeight: '75vh', padding: '5px', overflow: 'auto' }}>
        <Alert severity="info">Brak paragonów do skorygowania</Alert>
      </Box>
    );
  }

  React.useEffect(() => {
    if (_.isEmpty(editData)) {
      setInitProduct(
        { id: Date.now(), name: order.event_name, quantity: '1', unit: 'zal', pkwiu: '',
          gross_price: '400', net_price: '370.37', net_value: '370.37', vat_rate: '8',
          vat_amount: '29.63', gross_value: '400' });
    }
  }, [editData]);

  return (
    <form onSubmit={formik.handleSubmit}>
      {showAlert && (
        <AlertDialogContainer
          severity={severity}
          title={title}
          message={message}
          setShowAlert={setShowAlert}
        />
      )}
      <Box sx={{ maxHeight: '75vh', padding: '5px', overflow: 'auto' }}>
        <Card sx={{ marginTop: '20px' }}>
          <CardContent>
            <FormControl fullWidth error={formik.errors.receipt_correction_for && formik.touched.receipt_correction_for}>
              <InputLabel id="receipt_correction_for-label">Korygowany paragon</InputLabel>
              <Select
                id="receipt_correction_for"
                name="receipt_correction_for"
                labelId="receipt_correction_for-label"
                value={formik.values.receipt_correction_for}
                label="Korygowany paragon"
                onChange={formik.handleChange}
              >
                {!_.isNil(receipts) && receipts.map(receipt => <MenuItem key={receipt.id} value={receipt.id}>{receipt.number}</MenuItem>)}
              </Select>
              <FormHelperText>{formik.errors.receipt_correction_for}</FormHelperText>
            </FormControl>
          </CardContent>
        </Card>
        <Card sx={{ marginBottom: '20px', marginTop: '20px' }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <TextField
              sx={{ width: '100%' }}
              id="number"
              name="number"
              label="Numer paragonu korygowanego"
              value={formik.values.number}
              onChange={formik.handleChange}
              error={formik.errors.number && formik.touched.number}
              helperText={formik.errors.number}
            ></TextField>
          </CardContent>
        </Card>
        <BaseDataForm formik={formik} />
        <Stack sx={{ mt: 1, mb: 1 }}>
          <Position order={order} ref={productsRef} formik={formik} editData={editData} initProduct={initProduct} />
        </Stack>
        <Card sx={{ marginTop: '20px' }}>
          <CardContent>
            <Stack sx={{ marginBottom: '20px' }} direction="row" spacing={2}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  id="issue_date"
                  inputFormat="yyyy-MM-dd"
                  mask="____-__-__"
                  label="Data wystawienia"
                  value={formik.values.issue_date}
                  onChange={val => {
                    formik.setFieldValue('issue_date', formatDate(val));
                    updateInvoiceNumber(formatDate(val));
                  }}
                  renderInput={params => <TextField {...params} error={formik.errors.issue_date && formik.touched.issue_date} helperText={formik.errors.issue_date}/>}
                />
              </LocalizationProvider>
              <FormControl>
                <InputLabel id="payment_method-label">sposób płatności</InputLabel>
                <Select
                  id="payment_method"
                  name="payment_method"
                  labelId="payment_method-label"
                  value={formik.values.payment_method}
                  label="sposób płatności"
                  onChange={formik.handleChange}
                >
                  <MenuItem value="gotowka">Gotówka</MenuItem>
                  <MenuItem value="przelew">Przelew</MenuItem>
                  <MenuItem value="przedplata">Przedpłata</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <TextField
              id="remarks"
              multiline
              fullWidth
              rows={4}
              label="Uwagi"
              value={formik.values.remarks}
              onChange={formik.handleChange}
            />
            <Box height={20} />
            <TextField
              id="correction_reason"
              multiline
              fullWidth
              rows={4}
              label="Przyczyna korekty"
              value={formik.values.correction_reason}
              onChange={formik.handleChange}
            />
          </CardContent>
        </Card>
      </Box>
      <Stack sx={{ marginTop: '20px' }} flexDirection="row" justifyContent="space-around">
        <Button sx={{ flexGrow: 0.35 }} type="submit" variant="outlined" onClick={handleClose}>anuluj</Button>
        <Button sx={{ flexGrow: 0.35 }} type="submit" variant="contained">{!_.isEmpty(editData) ? 'edytuj' : 'dodaj'}</Button>
      </Stack>
    </form>
  );
};
